import { formatBareUrl, formatInstanceUrl, peekUrlDisplayId, peekUrlInstanceId } from '@signage-player/device-browser';

const PICKER_RETURN_KEY = 'sp:picker-return';

let currentUrlKey: string | null = null;

// What a history entry boots into: instance id plus the optional display
// segment (#/<id>/<displayId>) — a display switch is a different player.
function urlKey(): string | null {
  const id = peekUrlInstanceId();
  if (!id) return null;
  const displayId = peekUrlDisplayId();
  return displayId === null ? id : `${id}/${displayId}`;
}

/**
 * Marks the next bare-URL boot as a deliberate return to the picker, so it
 * shows without the auto-start countdown (which would re-adopt the instance
 * the user just left).
 */
export function markPickerReturn(): void {
  try { sessionStorage.setItem(PICKER_RETURN_KEY, '1'); } catch { /* ignore */ }
}

/** Read-and-clear, called on every boot so a stale mark can't outlive one load. */
export function consumePickerReturn(): boolean {
  try {
    const marked = sessionStorage.getItem(PICKER_RETURN_KEY) !== null;
    sessionStorage.removeItem(PICKER_RETURN_KEY);
    return marked;
  } catch {
    return false;
  }
}

/**
 * Pushes the chosen identity as a new history entry (not replaceState), so
 * the bare-URL picker stays behind it and browser Back lands there.
 */
export function pushInstanceUrl(id: string, displayId: number | null = null): void {
  window.history.pushState({ fromPicker: true }, '', formatInstanceUrl(id, displayId));
  currentUrlKey = urlKey();
}

/**
 * Leaves the running player for the picker: back over the picker's own entry
 * when this one was pushed from it, else a fresh bare-URL load.
 */
export function navigateToPicker(): void {
  markPickerReturn();
  if ((window.history.state as { fromPicker?: unknown } | null)?.fromPicker === true) {
    window.history.back();
    return;
  }
  window.location.href = formatBareUrl();
}

/**
 * Back/Forward between the picker entry and a player entry is a same-document
 * traversal (popstate, no load) and the running player can't be torn down in
 * place — reload instead: a bare URL boots into the picker, an id URL into
 * that player. Popstates that leave the identity unchanged are ignored.
 */
export function installHistoryNavigation(): void {
  currentUrlKey = urlKey();
  window.addEventListener('popstate', () => {
    const key = urlKey();
    if (key === currentUrlKey) return;
    if (!key) markPickerReturn();
    window.location.reload();
  });
  // A bfcache-restored picker document is already showing; don't let the mark
  // set for it leak into a later cold start.
  window.addEventListener('pageshow', e => {
    if (e.persisted) consumePickerReturn();
  });
}
